import type { DashboardOverview } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { formatNumber } from "@/utils/tools";
import { ErrorBoundary } from "@st-marketplace/ui";
import type { FC, ReactNode } from "react";
import {
	AddOrderButton,
	AddOrderDialogHost,
	DashboardRecentOrders,
	DashboardSection,
	DashboardSellerPrices,
	StatTile,
} from "./components";

interface DashboardBuyViewProps {
	loading: boolean;
	error: Error | null;
	data: DashboardOverview;
	refetch: () => void;
	filter: ReactNode;
}

/** Buyer home: what you owe, what you ordered, who you buy from. */
const DashboardBuyView: FC<DashboardBuyViewProps> = ({
	loading,
	error,
	data,
	refetch,
	filter,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-8">
			<DashboardSection
				title={t("home.overview")}
				icon="line-chart-line"
				trailing={filter}
			>
				<ErrorBoundary
					isLoading={false}
					error={error ?? undefined}
					onReset={refetch}
				>
					<div className="dashboard-stats-grid">
						<StatTile
							label={t("home.totalDebtsToUsers")}
							value={formatNumber(data.debts_to_pay, "price")}
							icon="wallet-3-line"
							loading={loading}
						/>
						<StatTile
							label={t("home.totalOrdered")}
							value={formatNumber(data.total_ordered, "large_count")}
							icon="shopping-bag-3-line"
							loading={loading}
						/>
					</div>
				</ErrorBoundary>
			</DashboardSection>

			<DashboardSellerPrices />
			<DashboardRecentOrders
				side="buyer"
				title={t("home.recentOrders")}
				action={<AddOrderButton />}
				limit={10}
			/>

			<AddOrderDialogHost />
		</div>
	);
};

export default DashboardBuyView;
